import { Building2, DollarSign, Gauge, Percent, ShieldCheck, TrendingUp } from 'lucide-react';

type SnapshotScore = {
  label: string;
  value: number;
  description: string;
};

type SnapshotMetric = {
  label: string;
  value: string;
  subtext: string;
  icon: 'cashflow' | 'return' | 'caprate' | 'debt' | 'invested';
};

type FinancialSnapshotProps = {
  scores: {
    performance: SnapshotScore;
    stability: SnapshotScore;
    efficiency: SnapshotScore;
  };
  metrics: SnapshotMetric[];
};

const scoreTone = (score: number) => {
  if (score >= 75) {
    return {
      border: 'border-green-200',
      bg: 'bg-green-50',
      value: 'text-green-600'
    };
  }

  if (score >= 50) {
    return {
      border: 'border-amber-200',
      bg: 'bg-amber-50',
      value: 'text-amber-600'
    };
  }

  return {
    border: 'border-rose-200',
    bg: 'bg-rose-50',
    value: 'text-rose-600'
  };
};

const metricIcon = (icon: SnapshotMetric['icon']) => {
  switch (icon) {
    case 'cashflow':
      return <DollarSign size={18} />;
    case 'return':
      return <Percent size={18} />;
    case 'caprate':
      return <TrendingUp size={18} />;
    case 'debt':
      return <Gauge size={18} />;
    case 'invested':
      return <Building2 size={18} />;
    default:
      return <ShieldCheck size={18} />;
  }
};

const ScoreCard = ({ score }: { score: SnapshotScore }) => {
  const tone = scoreTone(score.value);

  return (
    <div className={`rounded-2xl border ${tone.border} ${tone.bg} p-5 shadow-sm`}>
      <p className="text-sm font-medium text-slate-500">{score.label}</p>
      <p className={`mt-3 text-3xl font-semibold ${tone.value}`}>{score.value}</p>
      <p className="mt-3 text-sm leading-6 text-slate-500">{score.description}</p>
    </div>
  );
};

const FinancialSnapshot = ({ scores, metrics }: FinancialSnapshotProps) => {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
        <ScoreCard score={scores.performance} />
        <ScoreCard score={scores.stability} />
        <ScoreCard score={scores.efficiency} />
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5">
        {metrics.map((metric) => (
          <div key={metric.label} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between gap-3">
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-2 text-slate-500">
                {metricIcon(metric.icon)}
              </div>
              <p className="text-right text-sm font-medium text-slate-500">{metric.label}</p>
            </div>
            <p className="mt-4 text-2xl font-semibold text-slate-900">{metric.value}</p>
            <p className="mt-2 text-sm leading-6 text-slate-500">{metric.subtext}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

export default FinancialSnapshot;
